import { SelectToolToggle } from '~/components/Toolbelt/components/SelectToolToggle'
import { ToolbeltSectionHeader } from '~/components/Toolbelt/components/ToolbeltSectionHeader'
import { ToolSpecificActions } from '~/components/Toolbelt/components/ToolSpecificActions'
import LayerKeys from '~/config/layerKeys'
import { getLayerIcon, getLayerLabel } from '~/config/layers'
import ObjectClassName from '~/config/objectClassNames'
import { Tools } from '~/config/tools'
import { useAppSelector } from '~/store/hooks'
import { Icon } from '~/ui/Icon'

const classNameToLayerKey: Partial<Record<ObjectClassName, LayerKeys>> = {
  ComfortZone: 'COMFORT_ZONES',
  Dimension: 'DIMENSIONS',
  Door: 'DOORS',
  ElevationLine: 'ELEVATION_LINE',
  ElevationPoint: 'ELEVATION_POINT',
  Obstruction: 'OBSTRUCTIONS',
  Product: 'PRODUCTS',
  Roof: 'ROOFS',
  RoofSection: 'ROOF_SECTIONS',
  UtilityBox: 'UTILITY_BOXES',
}

const toolNameToLayerKey: Record<Tools, LayerKeys | null | ((isExteriorWallLayer: boolean) => LayerKeys)> = {
  COMFORT_ZONE_LINE_TOOL: 'COMFORT_ZONES',
  COMFORT_ZONE_TOOL: 'COMFORT_ZONES',
  DIMENSION_TOOL: 'DIMENSIONS',
  DOOR_PRODUCT_TOOL: 'DOORS',
  DOOR_TOOL: 'DOORS',
  ELEVATION_LINE_TOOL: 'ROOFS',
  ELEVATION_POINT_TOOL: 'ROOFS',
  MOVE_TOOL: 'BACKGROUND_IMAGE',
  OBSTRUCTION_BOX_TOOL: 'OBSTRUCTIONS',
  OBSTRUCTION_CIRCLE_TOOL: 'OBSTRUCTIONS',
  OBSTRUCTION_POLYGON_TOOL: 'OBSTRUCTIONS',
  OBSTRUCTION_TOOL: 'OBSTRUCTIONS',
  PRODUCT_TOOL: 'PRODUCTS',
  UTILITY_BOX_TOOL: 'UTILITY_BOXES',
  ELLIPSE_TOOL: (isExteriorWallLayer: boolean) => (isExteriorWallLayer ? 'EXTERIOR_WALLS' : 'INTERIOR_WALLS'),
  LINE_TOOL: (isExteriorWallLayer: boolean) => (isExteriorWallLayer ? 'EXTERIOR_WALLS' : 'INTERIOR_WALLS'),
  RECTANGLE_TOOL: (isExteriorWallLayer: boolean) => (isExteriorWallLayer ? 'EXTERIOR_WALLS' : 'INTERIOR_WALLS'),
  SELECT_TOOL: null,
}

export const ToolbeltActions = () => {
  const activeTool = useAppSelector(state => state.tools.activeTool)
  const currentLayer = useAppSelector(state => state.layers.currentLayer)
  const selectedObjects = useAppSelector(state => state.selectedObjects)
  const selectedObjectClasses = Array.from(
    new Set(selectedObjects.map(({ className }) => className as ObjectClassName))
  )
  const segments = useAppSelector(state => state.objects.present.segments)
  const selectedWallLayers = Array.from(
    selectedObjects.reduce<Set<LayerKeys>>((wallLayersSelected, current) => {
      if (current.className === ObjectClassName.WALL || current.className === ObjectClassName.WALL_SEGMENT) {
        const segment = segments[current.id]
        if (segment) wallLayersSelected.add(segment.layerKey)
      }
      return wallLayersSelected
    }, new Set())
  )
  const toolLayer = toolNameToLayerKey[activeTool]
  const selectedLayer =
    (typeof toolLayer === 'string' ? toolLayer : toolLayer?.(currentLayer === 'EXTERIOR_WALLS')) ??
    (selectedWallLayers.length === 1
      ? selectedWallLayers[0]
      : selectedObjectClasses.length === 0
        ? currentLayer
        : (classNameToLayerKey[selectedObjectClasses[0]] ?? currentLayer))
  const isMultipleObjectTypes = selectedObjectClasses.length > 1
  const label = isMultipleObjectTypes
    ? 'MULTIPLE'
    : (getLayerLabel(selectedLayer, { includeOverrides: true }) ?? 'Layers')
  const icon = isMultipleObjectTypes
    ? 'multiSelect'
    : (getLayerIcon(selectedLayer ?? currentLayer, { includeOverrides: true }) ?? 'layers')
  return (
    <>
      <ToolbeltSectionHeader title={label} icon={<Icon name={icon} size="12" />} />
      <SelectToolToggle />
      <ToolSpecificActions selectedLayer={selectedLayer} />
    </>
  )
}
